import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search,
  Eye,
  Package,
  Calendar,
  DollarSign,
  Truck,
  Download,
  FileText,
  User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const MyOrdersCRM = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      
      // Check if searching by agent name (format: "Agent name")
      const agentMatch = searchLower.match(/^agent\s+(.+)/);
      if (agentMatch) {
        const agentName = agentMatch[1].toLowerCase();
        const filtered = orders.filter(order => {
          const agent = order.assigned_employee || {};
          return agent.full_name && agent.full_name.toLowerCase().includes(agentName);
        });
        setFilteredOrders(filtered);
        return;
      }
      
      const filtered = orders.filter(order => {
        const vehicle = order.vehicle || {};
        const customer = order.customer || {};
        const agent = order.assigned_employee || {};
        return (
          (order.id && order.id.toLowerCase().includes(searchLower)) ||
          (vehicle.model && vehicle.model.toLowerCase().includes(searchLower)) ||
          (vehicle.stock_no && vehicle.stock_no.toLowerCase().includes(searchLower)) ||
          (customer.full_name && customer.full_name.toLowerCase().includes(searchLower)) ||
          (agent.full_name && agent.full_name.toLowerCase().includes(searchLower))
        );
      });
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders);
    }
  }, [searchQuery, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marketplace/orders`);
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsDialogOpen(true);
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice-download`, {
        responseType: 'blob'
      });
      
      if (response.data.size === 0) {
        toast.error('Invoice file is empty. Please contact support.');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      if (error.response?.status === 404) {
        toast.error('Invoice not found. Please generate an invoice first.');
      } else {
        toast.error('Failed to download invoice');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      reserved: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-orange-100 text-orange-800',
      purchased: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      sold: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
    if (vehicleImageMatch) {
      return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
    }
    return `${BACKEND_URL}${imagePath}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">My Orders</h2>
          <p className="text-slate-600 mt-1">
            {user?.role === 'customer' 
              ? 'Track your vehicle orders and payments'
              : 'View orders assigned to you'
            }
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {filteredOrders.length} {filteredOrders.length === 1 ? 'Order' : 'Orders'}
        </Badge>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder='Search by Order ID, Model, Stock No, Customer, or "Agent name"...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Orders Found</h3>
              <p className="text-slate-600">
                {searchQuery ? 'Try adjusting your search query' : 'No orders have been assigned to you yet'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Total (USD)</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const vehicle = order.vehicle || {};
                  const customer = order.customer || {};
                  const payment = order.payment_summary || {};
                  
                  return (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">
                        #{order.id?.slice(-8).toUpperCase()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {vehicle.images && vehicle.images.length > 0 ? (
                            <img
                              src={getImageUrl(vehicle.images[0])}
                              alt="Vehicle"
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <Package className="h-10 w-10 text-slate-300" />
                          )}
                          <div>
                            <p className="font-medium">{vehicle.year} {vehicle.brand} {vehicle.model}</p>
                            <p className="text-xs text-slate-500">Stock: {vehicle.stock_no}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {customer.full_name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5 text-slate-400" />
                          <span>{order.assigned_employee?.full_name || 'Unassigned'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatDate(order.order_date)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(order.total_amount_usd, 'USD')}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Progress value={payment.paid_percentage || 0} className="h-2 w-24" />
                          <p className="text-xs text-slate-500">
                            {payment.paid_percentage || 0}% paid
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {(order.invoice_url || order.proforma_invoice_url || order.final_invoice_url) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadInvoice(order.id)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Order Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Complete information about this order
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 mt-4">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Customer Information</h3>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2 text-sm">
                  {selectedOrder.customer ? (
                    <>
                      <p><span className="font-medium text-slate-700">Name:</span> <span className="text-slate-900">{selectedOrder.customer.full_name}</span></p>
                      <p><span className="font-medium text-slate-700">Email:</span> <span className="text-slate-900">{selectedOrder.customer.email}</span></p>
                      <p><span className="font-medium text-slate-700">Phone:</span> <span className="text-slate-900">{selectedOrder.customer.phone || 'Not provided'}</span></p>
                    </>
                  ) : (
                    <p className="text-slate-500">Customer information not available</p>
                  )}
                </div>
              </div>

              {/* Vehicle Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Vehicle Information</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  {selectedOrder.vehicle?.images && selectedOrder.vehicle.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {selectedOrder.vehicle.images.slice(0, 3).map((img, idx) => (
                        <img
                          key={idx}
                          src={getImageUrl(img)}
                          alt={`Vehicle ${idx + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Model</p>
                      <p className="font-medium">{selectedOrder.vehicle?.year} {selectedOrder.vehicle?.model}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Stock No</p>
                      <p className="font-medium">{selectedOrder.vehicle?.stock_no}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Chassis No</p>
                      <p className="font-medium">{selectedOrder.vehicle?.chassis_no}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Status</p>
                      <Badge className={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge>
                    </div>
                    <div>
                      <p className="text-slate-500">Assigned Agent</p>
                      <p className="font-medium">{selectedOrder.assigned_employee?.full_name || 'Unassigned'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Payment Information</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Payment Progress</span>
                      <span className="font-semibold">{selectedOrder.payment_summary?.paid_percentage || 0}%</span>
                    </div>
                    <Progress value={selectedOrder.payment_summary?.paid_percentage || 0} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Total Amount</p>
                      <p className="font-medium">{formatCurrency(selectedOrder.total_amount_usd, 'USD')}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Total Paid</p>
                      <p className="font-medium text-green-600">{formatCurrency(selectedOrder.payment_summary?.total_paid_usd, 'USD')}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Balance</p>
                      <p className="font-medium text-red-600">{formatCurrency(selectedOrder.payment_summary?.balance_usd, 'USD')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4">
                {selectedOrder.invoice_url || selectedOrder.proforma_invoice_url || selectedOrder.final_invoice_url ? (
                  <Button onClick={() => handleDownloadInvoice(selectedOrder.id)} className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    Download Invoice
                  </Button>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-600">No invoice available yet</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOrdersCRM;
